import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Skeleton,
  Popover,
  MenuItem,
  MenuList,
  ListItemText,
  Checkbox,
  Avatar,
  Tooltip,
  CircularProgress,
  Collapse,
} from '@mui/material'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { format, formatDistanceToNow, isValid, subDays, startOfDay, endOfDay } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { useQuery } from '@hapana/shared/web/services/gqty'
import { useSupportAssistant } from '../support-assistant.hooks'
import {
  formatActionName,
  ACTION_LABELS,
  ACTION_CATEGORIES,
  RESOURCE_LABELS_SIMPLE,
  RESOURCE_COLORS_SIMPLE,
  SEVERITY_CONFIG,
  SOURCE_LABELS,
} from '@hapana/shared/web/components/shared/audit-table/audit-table.constants'
import {
  JsonDiffTable,
  tryParseJson,
} from '@hapana/shared/web/components/shared/audit-table/json-diff-table.component'
import { getAvatarColor } from '_utils/avatar-color.util'
import { useGetCorporateBySubdomainQuery } from '_hooks/use-coporate-integrations.hooks'
import { useGetSiteById } from '_hooks/gqty/sites.hooks'
import { extractSubdomain } from '_services/tenant.service'
import useSiteStore from '_stores/site.store'
import * as styles from './activity-tab.styles'

const URL_PREFIX = extractSubdomain()

const PAGE_SIZE = 50

/** Parse audit timestamp — may be epoch ms (number or numeric string) or ISO string */
function parseAuditTimestamp(ts: any): Date | null {
  if (!ts) return null
  // Try as numeric (epoch ms) first — this is how the audit API returns timestamps
  const asNum = typeof ts === 'string' ? Number(ts) : ts
  if (!isNaN(asNum) && asNum > 0) {
    const d = new Date(asNum)
    if (isValid(d)) return d
  }
  // Fallback: try as ISO string
  const d = new Date(ts)
  return isValid(d) ? d : null
}

/** Safely parse a JSON string, returning null on failure */
function safeParseJson(str: any): Record<string, any> | null {
  if (!str || typeof str !== 'string') return null
  try {
    return JSON.parse(str)
  } catch {
    return null
  }
}

// Date range presets
const DATE_PRESETS = [
  { key: 'today', days: 0 },
  { key: 'last7', days: 7 },
  { key: 'last30', days: 30 },
  { key: 'last90', days: 90 },
] as const

type DatePresetKey = (typeof DATE_PRESETS)[number]['key']

/**
 * Customer-visible audit actions for the core web app.
 * Excludes: Admin, Corporate, GDPR, Data deletion, Knowledge hub,
 * AI assistant, Demo, System config, Grow internal, Page navigation,
 * and Impersonation events.
 */
const CUSTOMER_VISIBLE_ACTIONS = new Set([
  // Member Events
  'MEMBER_CREATED',
  'MEMBER_UPDATED',
  'MEMBER_DELETED',
  'MEMBER_LOGIN',
  'MEMBER_LOGOUT',
  'MEMBER_CHECKIN',
  'MEMBER_CHECKOUT',
  'MEMBER_PROFILE_VIEWED',
  'MEMBER_MERGED',

  // Session/Booking Events
  'SESSION_BOOKED',
  'SESSION_CANCELLED',
  'SESSION_ATTENDED',
  'SESSION_NO_SHOW',
  'SESSION_WAITLISTED',
  'SESSION_CREATED',
  'SESSION_UPDATED',
  'SESSION_DELETED',

  // Payment Events
  'PAYMENT_PROCESSED',
  'PAYMENT_FAILED',
  'PAYMENT_REFUNDED',
  'PAYMENT_METHOD_ADDED',
  'PAYMENT_METHOD_REMOVED',
  'INVOICE_CREATED',
  'INVOICE_UPDATED',
  'INVOICE_PAID',
  'INVOICE_VOIDED',

  // Membership Events
  'MEMBERSHIP_CREATED',
  'MEMBERSHIP_UPDATED',
  'MEMBERSHIP_CANCELLED',
  'MEMBERSHIP_SUSPENDED',
  'MEMBERSHIP_RESUMED',
  'MEMBERSHIP_RENEWED',

  // Staff Events
  'STAFF_LOGIN',
  'STAFF_LOGOUT',
  'STAFF_CREATED',
  'STAFF_UPDATED',
  'STAFF_DELETED',
  'STAFF_ROLE_CHANGED',

  // Site Events (not Corporate)
  'SITE_CREATED',
  'SITE_UPDATED',

  // Door/Access Events
  'DOOR_ACCESS_GRANTED',
  'DOOR_ACCESS_DENIED',
  'DOOR_UNLOCKED',
  'DOOR_LOCKED',

  // Settings Events
  'SETTING_UPDATED',
  'SITE_SETTING_UPDATED',
  'BRAND_SETTING_UPDATED',

  // Communication Events
  'EMAIL_SENT',
  'EMAIL_DELIVERED',
  'EMAIL_OPENED',
  'EMAIL_BOUNCED',
  'EMAIL_COMPLAINED',
  'EMAIL_CLICKED',
  'EMAIL_FAILED',
  'EMAIL_SUPPRESSED',
  'SMS_SENT',
  'SMS_DELIVERED',
  'PUSH_NOTIFICATION_SENT',
  'PUSH_NOTIFICATION_DELIVERED',

  // Notes Events
  'NOTE_CREATED',
  'NOTE_UPDATED',

  // Ecommerce Events
  'ECOMMERCE_PURCHASE',

  // Dispute Events
  'DISPUTE_CREATED',
  'DISPUTE_WON',
  'DISPUTE_LOST',

  // Webhook Events
  'WEBHOOK_CREATED',
  'WEBHOOK_UPDATED',
  'WEBHOOK_DELETED',
  'WEBHOOK_ENABLED',
  'WEBHOOK_DISABLED',
  'WEBHOOK_DELIVERY_SUCCEEDED',
  'WEBHOOK_DELIVERY_FAILED',
])

// Build the filtered action list for the dropdown
const ALL_ACTIONS = Object.entries(ACTION_LABELS)
  .filter(([key]) => CUSTOMER_VISIBLE_ACTIONS.has(key))
  .map(([key, label]) => ({
    key,
    label: label as string,
  }))

// Extra categories not in the shared ACTION_CATEGORIES but relevant for the activity tab.
// Labels match the shared audit-table constants convention (English identifiers used as
// group headers in the action filter popover). These are consistent with ACTION_CATEGORIES
// from @hapana/shared — when that module is i18n-ised, these should follow.
const EXTRA_CATEGORIES: Record<string, { label: string; actions: string[] }> = {
  settings: {
    label: 'Settings',
    actions: ['SETTING_UPDATED', 'SITE_SETTING_UPDATED', 'BRAND_SETTING_UPDATED'],
  },
  communication: {
    label: 'Communication',
    actions: [
      'EMAIL_SENT',
      'EMAIL_DELIVERED',
      'EMAIL_OPENED',
      'EMAIL_BOUNCED',
      'EMAIL_COMPLAINED',
      'EMAIL_CLICKED',
      'EMAIL_FAILED',
      'EMAIL_SUPPRESSED',
      'SMS_SENT',
      'SMS_DELIVERED',
      'PUSH_NOTIFICATION_SENT',
      'PUSH_NOTIFICATION_DELIVERED',
    ],
  },
  notes: {
    label: 'Notes',
    actions: ['NOTE_CREATED', 'NOTE_UPDATED'],
  },
  disputes: {
    label: 'Disputes',
    actions: ['DISPUTE_CREATED', 'DISPUTE_WON', 'DISPUTE_LOST'],
  },
  webhooks: {
    label: 'Webhooks',
    actions: [
      'WEBHOOK_CREATED',
      'WEBHOOK_UPDATED',
      'WEBHOOK_DELETED',
      'WEBHOOK_ENABLED',
      'WEBHOOK_DISABLED',
      'WEBHOOK_DELIVERY_SUCCEEDED',
      'WEBHOOK_DELIVERY_FAILED',
    ],
  },
}

// Build grouped action categories from shared categories + extra categories
const GROUPED_ACTIONS = [
  ...Object.entries(ACTION_CATEGORIES).map(([_groupKey, group]) => {
    const visibleActions = (group as any).actions.filter((a: string) =>
      CUSTOMER_VISIBLE_ACTIONS.has(a)
    )
    if (visibleActions.length === 0) return null
    return {
      label: (group as any).label as string,
      actions: visibleActions.map((a: string) => ({
        key: a,
        label: (ACTION_LABELS[a as keyof typeof ACTION_LABELS] || formatActionName(a)) as string,
      })),
    }
  }),
  ...Object.values(EXTRA_CATEGORIES).map((group) => {
    const visibleActions = group.actions.filter((a) => CUSTOMER_VISIBLE_ACTIONS.has(a))
    if (visibleActions.length === 0) return null
    return {
      label: group.label,
      actions: visibleActions.map((a) => ({
        key: a,
        label: (ACTION_LABELS[a as keyof typeof ACTION_LABELS] || formatActionName(a)) as string,
      })),
    }
  }),
].filter(Boolean) as Array<{ label: string; actions: Array<{ key: string; label: string }> }>

// Extracted into its own component so the search input only re-renders the popover,
// not the entire ActivityTab (which includes the heavy event list + GQty query).
function ActionFilterPopover({
  anchorEl,
  onClose,
  selectedActions,
  setSelectedActions,
}: {
  anchorEl: HTMLElement | null
  onClose: () => void
  selectedActions: string[]
  setSelectedActions: React.Dispatch<React.SetStateAction<string[]>>
}) {
  const { t } = useTranslation('core')
  const [searchText, setSearchText] = useState('')

  const handleClose = () => {
    onClose()
    setSearchText('')
  }

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: styles.actionFilterPopoverPaperSx,
        },
      }}
    >
      <Box sx={styles.actionFilterSearchBoxSx}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder={t('supportAssistant.activity.searchActions')}
          value={searchText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={styles.actionFilterSearchIconSx} />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchText('')}
                  edge="end"
                  sx={styles.actionFilterClearButtonSx}
                >
                  <CloseIcon sx={styles.actionFilterClearIconSx} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: styles.actionFilterSearchInputSx,
          }}
        />
      </Box>
      <MenuList dense sx={styles.actionFilterMenuListSx}>
        {(() => {
          const search = searchText.toLowerCase()
          let hasResults = false
          const elements = GROUPED_ACTIONS.map((group) => {
            const filtered = search
              ? group.actions.filter((a) => a.label.toLowerCase().includes(search))
              : group.actions
            if (filtered.length === 0) return null
            hasResults = true
            const allGroupSelected = group.actions.every((a) => selectedActions.includes(a.key))
            const someGroupSelected = group.actions.some((a) => selectedActions.includes(a.key))
            return (
              <Box key={group.label}>
                <MenuItem
                  sx={styles.actionFilterGroupMenuItemSx}
                  onClick={() => {
                    if (allGroupSelected) {
                      setSelectedActions((prev: string[]) =>
                        prev.filter((a: string) => !group.actions.some((ga) => ga.key === a))
                      )
                    } else {
                      setSelectedActions((prev: string[]) => {
                        const newKeys = group.actions
                          .map((a) => a.key)
                          .filter((k) => !prev.includes(k))
                        return [...prev, ...newKeys]
                      })
                    }
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={allGroupSelected}
                    indeterminate={someGroupSelected && !allGroupSelected}
                    sx={styles.actionFilterGroupCheckboxSx}
                  />
                  <ListItemText
                    primary={group.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                    }}
                  />
                </MenuItem>
                {filtered.map((action) => (
                  <MenuItem
                    key={action.key}
                    sx={styles.actionFilterActionMenuItemSx}
                    onClick={() => {
                      setSelectedActions((prev: string[]) =>
                        prev.includes(action.key)
                          ? prev.filter((a: string) => a !== action.key)
                          : [...prev, action.key]
                      )
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedActions.includes(action.key)}
                      sx={styles.actionFilterActionCheckboxSx}
                    />
                    <ListItemText
                      primary={action.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontSize: '0.75rem',
                        noWrap: true,
                      }}
                    />
                  </MenuItem>
                ))}
              </Box>
            )
          })
          if (!hasResults) {
            return (
              <Box sx={styles.actionFilterNoResultsSx}>
                <Typography variant="caption" color="text.disabled">
                  {t('supportAssistant.activity.noActionsFound')}
                </Typography>
              </Box>
            )
          }
          return elements
        })()}
      </MenuList>
    </Popover>
  )
}

interface ActivityTabProps {
  fullWidth?: boolean
}

export function ActivityTab({ fullWidth }: ActivityTabProps = {}) {
  const { t } = useTranslation('core')
  const { brandId, locationId } = useSupportAssistant()

  const { data: corporate } = useGetCorporateBySubdomainQuery(URL_PREFIX) as { data: any }
  const corporateId: string = corporate?.corporateBySubdomain?.corporateId || brandId || ''

  // Site timezone — use selector to subscribe to store changes
  const currentSiteId = useSiteStore((state) => state.currentSite?.siteId)
  const { data: siteData } = useGetSiteById(currentSiteId || undefined)
  const siteTimezone: string | undefined =
    siteData?.settings?.location?.configurations?.timezone || undefined

  // State
  const [page, setPage] = useState(1)
  const [allEvents, setAllEvents] = useState<any[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [selectedActions, setSelectedActions] = useState<string[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [selectedUserLabel, setSelectedUserLabel] = useState<string | null>(null)
  const [selectedUserType, setSelectedUserType] = useState<'actioned' | 'affected'>('actioned')
  const [datePreset, setDatePreset] = useState<DatePresetKey | null>('last7')

  const [expandedEventId, setExpandedEventId] = useState<string | null>(null)

  // Popover anchors
  const [actionAnchor, setActionAnchor] = useState<HTMLElement | null>(null)
  const [dateAnchor, setDateAnchor] = useState<HTMLElement | null>(null)

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Build date range from preset
  const dateRange = useMemo(() => {
    if (!datePreset) return undefined
    const preset = DATE_PRESETS.find((p) => p.key === datePreset)
    if (!preset) return undefined
    const now = new Date()
    return {
      startDate: startOfDay(subDays(now, preset.days)).toISOString(),
      endDate: endOfDay(now).toISOString(),
    }
  }, [datePreset])

  // GQty query
  const query = useQuery({ suspense: false })

  // Always scope to customer-visible actions — use user selection if any, otherwise the full whitelist
  const actionsFilter = selectedActions.length > 0 ? selectedActions : [...CUSTOMER_VISIBLE_ACTIONS]

  const filterInput = useMemo(
    () => ({
      corporateId,
      ...(locationId && { siteId: locationId }),
      ...(searchText && { searchText }),
      actions: actionsFilter as any,
      ...(selectedUserId && selectedUserType === 'actioned' && { userId: selectedUserId }),
      ...(selectedUserId && selectedUserType === 'affected' && { affectedUserId: selectedUserId }),
      ...(dateRange && { dateRange }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- actionsFilter is derived from selectedActions
    [
      corporateId,
      locationId,
      searchText,
      selectedActions,
      selectedUserId,
      selectedUserType,
      dateRange,
    ]
  )

  const auditResult = corporateId
    ? query.auditEvents?.({
        filter: filterInput,
        sort: { field: 'timestamp' as any, direction: 'desc' as any },
        pagination: { page, pageSize: PAGE_SIZE },
      })
    : null

  // Access fields to trigger GQty loading, then filter out empty/useless events
  const currentPageEvents = useMemo(() => {
    if (!auditResult?.events) return []
    const raw = auditResult.events.filter(
      (e): e is NonNullable<typeof e> => e !== null && e !== undefined
    )
    for (const event of raw) {
      void event.id
      void event.userId
      void event.userEmail
      void event.userFirstName
      void event.userLastName
      void event.affectedUserId
      void event.affectedUserEmail
      void event.affectedUserFirstName
      void event.affectedUserLastName
      void event.siteId
      void event.siteName
      void event.action
      void event.resource
      void event.resourceId
      void event.timestamp
      void event.details?.summary
      void event.details?.changes
      void event.details?.metadataJson
      void event.details?.contextJson
      void event.details?.newValueJson
      void event.details?.reason
      void event.severity
      void event.source
      void event.ipAddress
      void event.pageUrl
      void event.pageName
      void event.visibility
    }
    // Filter out events with no meaningful content (no timestamp, no summary, no resource)
    return raw.filter((event) => {
      if (!event.timestamp) return false
      const hasSummary = Boolean(event.details?.summary)
      const hasResource = Boolean(event.resourceId)
      const hasUser = Boolean(event.userId || event.userEmail || event.userFirstName)
      return hasSummary || hasResource || hasUser
    })
  }, [auditResult?.events])

  const totalCount = auditResult?.pagination?.totalCount || 0
  void auditResult?.pagination?.currentPage
  void auditResult?.pagination?.pageSize
  void auditResult?.pagination?.totalPages
  void auditResult?.pagination?.hasPreviousPage
  void auditResult?.pagination?.hasNextPage
  void auditResult?.pagination?.totalCount

  const isLoading = query.$state.isLoading

  // Accumulate extra pages for infinite scroll (pages 2+).
  // Page 1 data comes directly from GQty's proxy (always reflects current filters).
  const currentPageKey = currentPageEvents.map((e: any) => e.id).join(',')
  useEffect(() => {
    if (page > 1 && currentPageEvents.length > 0) {
      setAllEvents((prev: any[]) => {
        const existingIds = new Set(prev.map((e: any) => e.id))
        const newEvents = currentPageEvents.filter((e: any) => !existingIds.has(e.id))
        if (newEvents.length === 0) return prev
        return [...prev, ...newEvents]
      })
    }
    if (currentPageEvents.length > 0 && currentPageEvents.length < PAGE_SIZE) {
      setHasMore(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageKey, page])

  // Display list: page 1 reads directly from GQty proxy (no stale state),
  // pages 2+ merge accumulated results with current page.
  const visibleEvents = useMemo(() => {
    if (page === 1) return currentPageEvents
    const merged = [...allEvents]
    for (const event of currentPageEvents) {
      if (!merged.some((e: any) => e.id === event.id)) {
        merged.push(event)
      }
    }
    return merged
  }, [currentPageEvents, allEvents, page])

  // Reset pagination on filter change
  useEffect(() => {
    setPage(1)
    setHasMore(true)
    setAllEvents([])
  }, [searchText, selectedActions, selectedUserId, selectedUserType, datePreset])

  // Infinite scroll — load next page when scrolled past 2/3
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el || isLoading || !hasMore) return
    const scrollRatio = (el.scrollTop + el.clientHeight) / el.scrollHeight
    if (scrollRatio > 0.66) {
      setPage((p: number) => p + 1)
    }
  }, [isLoading, hasMore])

  const handleRefresh = useCallback(() => {
    query.$refetch()
  }, [query])

  const clearAllFilters = useCallback(() => {
    setSearchText('')
    setSelectedActions([])
    setSelectedUserId(null)
    setSelectedUserLabel(null)
    setSelectedUserType('actioned')
    setDatePreset('last7')
    setPage(1)
    setHasMore(true)
  }, [])

  const hasActiveFilters =
    searchText.length > 0 ||
    selectedActions.length > 0 ||
    selectedUserId !== null ||
    (datePreset !== null && datePreset !== 'last7')

  // Click a user in the event row to filter by them
  const filterByUser = useCallback(
    (userId: string, displayName: string, type: 'actioned' | 'affected' = 'actioned') => {
      setSelectedUserId(userId)
      setSelectedUserLabel(displayName)
      setSelectedUserType(type)
    },
    []
  )

  const getDatePresetLabel = (key: DatePresetKey) => {
    switch (key) {
      case 'today':
        return t('supportAssistant.activity.dateToday')
      case 'last7':
        return t('supportAssistant.activity.dateLast7')
      case 'last30':
        return t('supportAssistant.activity.dateLast30')
      case 'last90':
        return t('supportAssistant.activity.dateLast90')
      default:
        return key
    }
  }

  const toggleExpand = useCallback((eventId: string) => {
    setExpandedEventId((prev: string | null) => (prev === eventId ? null : eventId))
  }, [])

  return (
    <Box sx={styles.activityTabRootSx}>
      {/* Search Bar */}
      <Box sx={styles.searchBarContainerSx(fullWidth)}>
        <TextField
          size="small"
          fullWidth
          autoFocus={fullWidth}
          placeholder={t('supportAssistant.activity.searchPlaceholder')}
          value={searchText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={styles.searchBarIconSx} />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchText('')} edge="end">
                  <CloseIcon sx={styles.searchBarClearIconSx} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={styles.searchBarTextFieldSx}
        />
      </Box>

      {/* Filter Chips Row */}
      <Box sx={styles.filterChipsRowSx(fullWidth)}>
        {/* Action filter */}
        <Chip
          icon={<FilterIcon sx={styles.filterChipIconSx} />}
          label={
            selectedActions.length > 0
              ? `${t('supportAssistant.activity.action')} (${selectedActions.length})`
              : t('supportAssistant.activity.action')
          }
          size="small"
          variant={selectedActions.length > 0 ? 'filled' : 'outlined'}
          onClick={(e: React.MouseEvent<HTMLDivElement>) =>
            setActionAnchor(e.currentTarget as HTMLElement)
          }
          onDelete={selectedActions.length > 0 ? () => setSelectedActions([]) : undefined}
          sx={selectedActions.length > 0 ? styles.activeChipSx : styles.chipSx}
        />

        {/* Date range filter */}
        <Chip
          icon={<CalendarIcon sx={styles.filterChipIconSx} />}
          label={datePreset ? getDatePresetLabel(datePreset) : t('supportAssistant.activity.date')}
          size="small"
          variant={datePreset ? 'filled' : 'outlined'}
          onClick={(e: React.MouseEvent<HTMLDivElement>) =>
            setDateAnchor(e.currentTarget as HTMLElement)
          }
          onDelete={datePreset ? () => setDatePreset(null) : undefined}
          sx={datePreset ? styles.activeChipSx : styles.chipSx}
        />

        {/* User filter (shown when active) */}
        {selectedUserId && (
          <Chip
            icon={<PersonIcon sx={styles.filterChipIconSx} />}
            label={`${selectedUserType === 'affected' ? t('supportAssistant.activity.affected') : ''}${selectedUserLabel || t('supportAssistant.activity.user')}`}
            size="small"
            variant="filled"
            onDelete={() => {
              setSelectedUserId(null)
              setSelectedUserLabel(null)
              setSelectedUserType('actioned')
            }}
            sx={styles.activeChipSx}
          />
        )}

        {/* Results count — hidden while loading */}
        {!isLoading && (
          <Typography variant="caption" color="text.secondary" sx={styles.resultsCountSx}>
            {totalCount} {t('supportAssistant.activity.results')}
          </Typography>
        )}

        {/* Spacer */}
        <Box sx={styles.spacerSx} />

        {/* Refresh */}
        <IconButton
          size="small"
          onClick={handleRefresh}
          disabled={isLoading}
          sx={styles.refreshButtonSx}
        >
          {isLoading && visibleEvents.length === 0 ? (
            <CircularProgress size={14} />
          ) : (
            <RefreshIcon sx={styles.refreshIconSx} />
          )}
        </IconButton>
      </Box>

      {/* Events List */}
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={styles.eventsListContainerSx(fullWidth)}
      >
        {isLoading && visibleEvents.length === 0 ? (
          <Box sx={styles.loadingSkeletonContainerSx}>
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={32} sx={styles.loadingSkeletonItemSx} />
            ))}
          </Box>
        ) : visibleEvents.length === 0 ? (
          <Box sx={styles.emptyStateSx}>
            <HistoryIcon sx={styles.emptyStateIconSx} />
            <Typography variant="body2" color="text.secondary" sx={styles.emptyStateTitleSx}>
              {t('supportAssistant.activity.noEvents')}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={styles.emptyStateSubtitleSx}>
              {hasActiveFilters
                ? t('supportAssistant.activity.tryAdjustingFilters')
                : t('supportAssistant.activity.noEventsDescription')}
            </Typography>
          </Box>
        ) : (
          <Box sx={styles.eventListColumnSx}>
            {visibleEvents.map((event: any, idx: number) => (
              <ActivityEventRow
                key={event.id ? `${event.id}` : `event-${idx}`}
                event={event}
                isExpanded={expandedEventId === event.id}
                onToggle={toggleExpand}
                onUserClick={filterByUser}
                siteTimezone={siteTimezone}
                fullWidth={fullWidth}
              />
            ))}
            {/* Loading indicator for next page */}
            {isLoading && visibleEvents.length > 0 && (
              <Box sx={styles.loadingMoreSx}>
                <CircularProgress size={18} />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Footer — total count */}
      {visibleEvents.length > 0 && (
        <Box sx={styles.footerSx(fullWidth)}>
          <Typography variant="caption" color="text.secondary">
            {!hasMore || visibleEvents.length >= totalCount
              ? visibleEvents.length
              : `${visibleEvents.length} / ${totalCount}`}{' '}
            {t('supportAssistant.activity.results')}
          </Typography>
        </Box>
      )}

      {/* Action Filter Popover — isolated component so search typing doesn't re-render the event list */}
      <ActionFilterPopover
        anchorEl={actionAnchor}
        onClose={() => setActionAnchor(null)}
        selectedActions={selectedActions}
        setSelectedActions={setSelectedActions}
      />

      {/* Date Range Popover */}
      <Popover
        open={Boolean(dateAnchor)}
        anchorEl={dateAnchor}
        onClose={() => setDateAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: styles.datePopoverPaperSx,
          },
        }}
      >
        <Box sx={styles.datePopoverHeaderSx}>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            textTransform="uppercase"
            letterSpacing={0.5}
          >
            {t('supportAssistant.activity.dateRange')}
          </Typography>
        </Box>
        <MenuList dense sx={styles.datePopoverMenuListSx}>
          {DATE_PRESETS.map((preset) => (
            <MenuItem
              key={preset.key}
              selected={datePreset === preset.key}
              onClick={() => {
                setDatePreset(datePreset === preset.key ? null : preset.key)
                setDateAnchor(null)
              }}
              sx={styles.datePopoverMenuItemSx}
            >
              <ListItemText
                primary={getDatePresetLabel(preset.key)}
                primaryTypographyProps={{ variant: 'body2', fontSize: '0.8125rem' }}
              />
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </Box>
  )
}

// ─── Event Row Component (Accordion style, like PostHog) ─────────────
interface ActivityEventRowProps {
  event: any
  isExpanded: boolean
  onToggle: (eventId: string) => void
  onUserClick: (userId: string, displayName: string, type?: 'actioned' | 'affected') => void
  siteTimezone?: string
  fullWidth?: boolean
}

const ActivityEventRow = React.memo(
  ({
    event,
    isExpanded,
    onToggle,
    onUserClick,
    siteTimezone,
    fullWidth = false,
  }: ActivityEventRowProps) => {
    const { t } = useTranslation('core')

    const actionLabel =
      ACTION_LABELS[event.action as keyof typeof ACTION_LABELS] ||
      formatActionName(event.action || '')
    const resourceLabel =
      RESOURCE_LABELS_SIMPLE[(event.resource || '').toLowerCase()] || event.resource || ''
    const resourceColor = RESOURCE_COLORS_SIMPLE[(event.resource || '').toLowerCase()] || 'default'

    const userName =
      event.userFirstName || event.userLastName
        ? `${event.userFirstName || ''} ${event.userLastName || ''}`.trim()
        : event.userEmail || t('supportAssistant.activity.system')

    const initials = userName
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)

    const affectedUserName =
      event.affectedUserFirstName || event.affectedUserLastName
        ? `${event.affectedUserFirstName || ''} ${event.affectedUserLastName || ''}`.trim()
        : event.affectedUserEmail || null

    const { timeAgo, displayDate, tzLabel } = useMemo(() => {
      const ts = parseAuditTimestamp(event.timestamp)
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
      const dTz = siteTimezone || browserTz
      return {
        timeAgo: ts ? formatDistanceToNow(ts, { addSuffix: true }) : '',
        displayDate:
          ts && dTz
            ? formatInTimeZone(ts, dTz, 'dd MMM yyyy, HH:mm:ss')
            : ts
              ? format(ts, 'dd MMM yyyy, HH:mm:ss')
              : '',
        tzLabel: ts && dTz ? formatInTimeZone(ts, dTz, 'zzz') : null,
      }
    }, [event.timestamp, siteTimezone])

    const severityConfig = event.severity
      ? SEVERITY_CONFIG[event.severity as keyof typeof SEVERITY_CONFIG]
      : null
    const sourceLabel = event.source
      ? SOURCE_LABELS[event.source as keyof typeof SOURCE_LABELS]
      : null
    const changes: any[] = event.details?.changes || []
    const summary = event.details?.summary || null
    const reason = event.details?.reason || null

    // Full resource identifier string (e.g. "Invoice #e376a808-...")
    const resourceIdDisplay = event.resourceId ? `#${String(event.resourceId)}` : null

    // Memoize expensive JSON parsing and context extraction — only recalculated when event changes
    const { contextDisplayItems, contextFieldKeys, metadata, context, newValueSnapshot } =
      useMemo(() => {
        const _metadata = safeParseJson(event.details?.metadataJson)
        const _context = safeParseJson(event.details?.contextJson)
        const _newValueSnapshot = safeParseJson(event.details?.newValueJson)
        const _changes: any[] = event.details?.changes || []

        const CONTEXT_DISPLAY_FIELDS: Record<string, string> = {
          clientName: t('supportAssistant.activity.contextLabels.client'),
          memberName: t('supportAssistant.activity.contextLabels.member'),
          clientFirstName: t('supportAssistant.activity.contextLabels.client'),
          totalAmount: t('supportAssistant.activity.contextLabels.amount'),
          amount: t('supportAssistant.activity.contextLabels.amount'),
          price: t('supportAssistant.activity.contextLabels.price'),
          currency: t('supportAssistant.activity.contextLabels.currency'),
          invoiceNumber: t('supportAssistant.activity.contextLabels.invoiceNumber'),
          membershipName: t('supportAssistant.activity.contextLabels.membership'),
          serviceName: t('supportAssistant.activity.contextLabels.service'),
          serviceTemplateName: t('supportAssistant.activity.contextLabels.package'),
          sessionName: t('supportAssistant.activity.contextLabels.session'),
          sessionDate: t('supportAssistant.activity.contextLabels.sessionDate'),
          templateName: t('supportAssistant.activity.contextLabels.template'),
          planName: t('supportAssistant.activity.contextLabels.plan'),
          status: t('supportAssistant.activity.contextLabels.status'),
        }

        const changesContext: Record<string, any> = {}
        for (const c of _changes) {
          if (c.field && c.newValue !== null && c.newValue !== undefined) {
            changesContext[c.field] = c.newValue
          }
        }

        const allContext: Record<string, any> = {
          ...changesContext,
          ...(_newValueSnapshot || {}),
          ...(_context || {}),
          ...(_metadata || {}),
        }

        const items = Object.entries(CONTEXT_DISPLAY_FIELDS)
          .map(([key, label]) => {
            const val = allContext[key]
            if (val === null || val === undefined || val === '' || typeof val === 'object')
              return null
            return { key, label, value: String(val) }
          })
          .filter(Boolean) as { key: string; label: string; value: string }[]

        if (!allContext.clientName && !allContext.memberName) {
          const first = allContext.clientFirstName || allContext.affectedUserFirstName || ''
          const last = allContext.clientLastName || allContext.affectedUserLastName || ''
          const fullName = `${first} ${last}`.trim()
          const clientLabel = t('supportAssistant.activity.contextLabels.client')
          if (fullName && !items.some((i) => i.label === clientLabel)) {
            items.unshift({ key: '_clientName', label: clientLabel, value: fullName })
          }
        }

        const amountItem = items.find(
          (i) => i.key === 'totalAmount' || i.key === 'amount' || i.key === 'price'
        )
        const currencyItem = items.find((i) => i.key === 'currency')
        if (amountItem && currencyItem) {
          amountItem.value = `${currencyItem.value} ${amountItem.value}`
          const currIdx = items.findIndex((i) => i.key === 'currency')
          if (currIdx !== -1) items.splice(currIdx, 1)
        }

        return {
          contextDisplayItems: items,
          contextFieldKeys: new Set(Object.keys(CONTEXT_DISPLAY_FIELDS)),
          metadata: _metadata,
          context: _context,
          newValueSnapshot: _newValueSnapshot,
        }
      }, [event.details, t])

    const formatVal = (v: any) =>
      v === ''
        ? t('supportAssistant.activity.formatValues.empty')
        : v === 'true'
          ? t('supportAssistant.activity.formatValues.yes')
          : v === 'false'
            ? t('supportAssistant.activity.formatValues.no')
            : (v ?? '—')

    return (
      <Box sx={styles.eventRowRootSx(isExpanded)}>
        {/* Collapsed row header */}
        <Box sx={styles.eventRowHeaderSx(isExpanded, fullWidth)} onClick={() => onToggle(event.id)}>
          <Box sx={styles.eventRowFlexSx}>
            {/* Left column: chevron + avatar */}
            <Box sx={styles.eventRowLeftColumnSx}>
              <ExpandMoreIcon sx={styles.expandIconSx(isExpanded)} />
              <Tooltip title={event.userId ? `Filter by ${userName}` : ''}>
                <Avatar
                  sx={
                    event.userId
                      ? styles.eventAvatarSx(getAvatarColor(userName))
                      : styles.eventAvatarNonClickableSx(getAvatarColor(userName))
                  }
                  onClick={(e: React.MouseEvent) => {
                    if (event.userId) {
                      e.stopPropagation()
                      onUserClick(event.userId, userName)
                    }
                  }}
                >
                  {initials}
                </Avatar>
              </Tooltip>
            </Box>

            {/* Right column: action row + summary */}
            <Box sx={styles.eventRowRightColumnSx}>
              {/* Action row */}
              <Box sx={styles.actionRowSx}>
                <Typography variant="caption" sx={styles.actionLabelSx}>
                  {actionLabel}
                </Typography>
                <Tooltip title={tzLabel ? `${displayDate} (${tzLabel})` : displayDate}>
                  <Typography variant="caption" color="text.disabled" sx={styles.timeAgoSx}>
                    {timeAgo}
                  </Typography>
                </Tooltip>
                <Chip
                  label={resourceLabel}
                  size="small"
                  color={resourceColor as any}
                  variant="outlined"
                  sx={styles.resourceChipSx}
                />
              </Box>

              {/* Summary + context hint — inline below action */}
              {!isExpanded && (summary || contextDisplayItems.length > 0 || resourceIdDisplay) && (
                <Typography variant="caption" color="text.secondary" sx={styles.summaryHintSx}>
                  {[
                    summary,
                    ...contextDisplayItems
                      .filter((i) => !summary?.includes(i.value))
                      .slice(0, 3)
                      .map((i) => `${i.label}: ${i.value}`),
                    ...(resourceIdDisplay && !summary?.includes(resourceIdDisplay)
                      ? [`${resourceLabel} ${resourceIdDisplay}`]
                      : []),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Expanded detail panel */}
        <Collapse in={isExpanded} timeout={200}>
          <Box sx={styles.expandedPanelSx(fullWidth)}>
            {/* Summary — prominent at the top if available */}
            {summary && (
              <Typography variant="body2" sx={styles.expandedSummarySx}>
                {summary}
              </Typography>
            )}

            {/* Resource context — key identifiers for what was affected */}
            {(resourceIdDisplay || contextDisplayItems.length > 0) && (
              <Box sx={styles.contextChipsContainerSx}>
                {resourceIdDisplay && (
                  <Chip
                    label={`${resourceLabel} ${resourceIdDisplay}`}
                    size="small"
                    variant="outlined"
                    sx={styles.resourceIdChipSx}
                  />
                )}
                {contextDisplayItems.map((item) => (
                  <Chip
                    key={item.key}
                    label={`${item.label}: ${item.value}`}
                    size="small"
                    variant="outlined"
                    sx={styles.contextItemChipSx}
                  />
                ))}
              </Box>
            )}

            {/* Property rows — PostHog-style key-value list */}
            <Box sx={styles.propertyRowsContainerSx}>
              {/* Timestamp — show site timezone if available */}
              <PropertyRow label={t('supportAssistant.activity.detail.timestamp')}>
                <Box>
                  <Typography variant="caption" sx={styles.timestampTextSx}>
                    {displayDate}
                  </Typography>
                  {tzLabel && (
                    <Typography variant="caption" color="text.disabled" sx={styles.tzLabelSx}>
                      ({tzLabel})
                    </Typography>
                  )}
                  {timeAgo && (
                    <Typography variant="caption" color="text.disabled" sx={styles.timeAgoDetailSx}>
                      ({timeAgo})
                    </Typography>
                  )}
                </Box>
              </PropertyRow>

              {/* Actioned by */}
              <PropertyRow label={t('supportAssistant.activity.detail.actionedBy')}>
                <Box
                  sx={styles.actionedByContainerSx(Boolean(event.userId))}
                  onClick={(e: React.MouseEvent) => {
                    if (event.userId) {
                      e.stopPropagation()
                      onUserClick(event.userId, userName)
                    }
                  }}
                >
                  <Avatar sx={styles.detailAvatarSx(getAvatarColor(userName))}>{initials}</Avatar>
                  <Typography className="prop-user" variant="caption" sx={styles.detailUserNameSx}>
                    {userName}
                  </Typography>
                </Box>
              </PropertyRow>

              {/* Affected user */}
              {affectedUserName && (
                <PropertyRow label={t('supportAssistant.activity.detail.affectedUser')}>
                  <Box
                    sx={styles.affectedUserContainerSx(Boolean(event.affectedUserId))}
                    onClick={(e: React.MouseEvent) => {
                      if (event.affectedUserId) {
                        e.stopPropagation()
                        onUserClick(event.affectedUserId, affectedUserName, 'affected')
                      }
                    }}
                  >
                    <Avatar
                      sx={styles.detailAvatarSx(
                        getAvatarColor(event.affectedUserId || affectedUserName)
                      )}
                    >
                      {affectedUserName
                        .split(' ')
                        .map((n: string) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)}
                    </Avatar>
                    <Typography
                      className="prop-affected-user"
                      variant="caption"
                      sx={styles.detailUserNameSx}
                    >
                      {affectedUserName}
                    </Typography>
                  </Box>
                </PropertyRow>
              )}

              {/* Location */}
              {event.siteName && (
                <PropertyRow
                  label={t('supportAssistant.activity.detail.location')}
                  value={event.siteName}
                />
              )}

              {/* Reason */}
              {reason && (
                <PropertyRow label={t('supportAssistant.activity.detail.reason')} value={reason} />
              )}

              {/* Metadata — render key-value pairs not already shown in context chips */}
              {metadata &&
                Object.entries(metadata).map(([key, val]) => {
                  if (key === 'timestamp') return null
                  if (val === null || val === undefined) return null
                  if (typeof val === 'object') return null
                  // Skip fields already shown in context chips
                  if (contextFieldKeys.has(key)) return null
                  return (
                    <PropertyRow
                      key={key}
                      label={key
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/_/g, ' ')
                        .trim()}
                      value={String(val)}
                    />
                  )
                })}

              {/* Metadata nested objects (e.g. stats from sync events) */}
              {metadata &&
                Object.entries(metadata).flatMap(([key, val]) => {
                  if (!val || typeof val !== 'object' || Array.isArray(val)) return []
                  return Object.entries(val as Record<string, any>)
                    .filter(
                      ([_, subVal]) =>
                        subVal !== null && subVal !== undefined && typeof subVal !== 'object'
                    )
                    .map(([subKey, subVal]) => (
                      <PropertyRow
                        key={`${key}.${subKey}`}
                        label={subKey
                          .replace(/([A-Z])/g, ' $1')
                          .replace(/_/g, ' ')
                          .trim()}
                        value={String(subVal)}
                      />
                    ))
                })}

              {/* Tags row */}
              <PropertyRow label={t('supportAssistant.activity.tags')}>
                <Box sx={styles.tagsContainerSx}>
                  <Chip
                    label={resourceLabel}
                    size="small"
                    color={resourceColor as any}
                    sx={styles.tagChipSx}
                  />
                  {severityConfig && (
                    <Chip
                      label={severityConfig.label}
                      size="small"
                      color={severityConfig.color as any}
                      sx={styles.tagChipSx}
                    />
                  )}
                  {sourceLabel && (
                    <Chip
                      label={sourceLabel}
                      size="small"
                      variant="outlined"
                      sx={styles.tagChipSx}
                    />
                  )}
                </Box>
              </PropertyRow>

              {/* Page */}
              {event.pageName && (
                <PropertyRow
                  label={t('supportAssistant.activity.detail.page')}
                  value={event.pageName}
                />
              )}

              {/* IP Address */}
              {event.ipAddress && (
                <PropertyRow label={t('supportAssistant.activity.detail.ipAddress')}>
                  <Typography variant="caption" sx={styles.ipAddressTextSx}>
                    {event.ipAddress}
                  </Typography>
                </PropertyRow>
              )}
            </Box>

            {/* Changes section */}
            {changes.length > 0 && (
              <Box sx={styles.changesSectionSx}>
                <Typography variant="caption" sx={styles.changesHeaderSx}>
                  {t('supportAssistant.activity.detail.changes')} ({changes.length})
                </Typography>
                {changes.map((c: any, i: number) => {
                  const label = c.displayName || c.field?.replace(/_/g, ' ')
                  const isJsonDiff = !!(tryParseJson(c.oldValue) || tryParseJson(c.newValue))
                  if (isJsonDiff) {
                    return (
                      <Box key={i} sx={styles.jsonDiffContainerSx}>
                        <Typography variant="caption" sx={styles.jsonDiffLabelSx}>
                          {label}
                        </Typography>
                        <JsonDiffTable oldValue={c.oldValue} newValue={c.newValue} />
                      </Box>
                    )
                  }
                  return (
                    <Box key={i} sx={styles.changeRowSx}>
                      <Typography variant="caption" sx={styles.changeFieldLabelSx}>
                        {label}
                      </Typography>
                      <Box sx={styles.changeValueContainerSx}>
                        <Typography variant="caption" sx={styles.oldValueSx}>
                          {formatVal(c.oldValue)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={styles.changeArrowSx}
                        >
                          →
                        </Typography>
                        <Typography variant="caption" sx={styles.newValueSx}>
                          {formatVal(c.newValue)}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    )
  }
)

// ─── Property Row (PostHog-style key/value) ─────────────────────────
interface PropertyRowProps {
  label: string
  value?: string
  subValue?: string
  children?: React.ReactNode
}

const PropertyRow = ({ label, value, subValue, children }: PropertyRowProps) => (
  <Box sx={styles.propertyRowRootSx}>
    <Typography variant="caption" sx={styles.propertyRowLabelSx}>
      {label}
    </Typography>
    <Box sx={styles.propertyRowValueContainerSx}>
      {children || (
        <Box sx={styles.propertyRowValueFlexSx}>
          <Typography variant="caption" sx={styles.propertyRowValueTextSx}>
            {value || '—'}
          </Typography>
          {subValue && (
            <Typography variant="caption" color="text.disabled" sx={styles.propertyRowSubValueSx}>
              {subValue}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  </Box>
)

export default ActivityTab
